import { DATE_FORMAT } from "@/shared/common/constants";
import type { IDefaultDateFilter } from "@/shared/common/types";
import { formatDate } from "@/shared/helpers";
import { IconCalendarRight } from "@ab.uitools/ui-library";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { Calendar } from "@ab.uitools/ui-library/components/Calendar";
import type { TDateFormat } from "@ab.uitools/ui-library/components/Calendar/types";
import { Chips } from "@ab.uitools/ui-library/components/Chips";
import classNames from "classnames";
import { type Dispatch, type ReactNode, type SetStateAction, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Props<T> {
  maxDate?: Date;
  filters: T;
  defaultFilters?: T;
  setFilters: Dispatch<SetStateAction<T>>;
  children?: ReactNode;
  resetPagination?: () => void;
}

export const FilterPanel = <T extends IDefaultDateFilter>({
  maxDate,
  filters,
  setFilters,
  children,
  defaultFilters,
  resetPagination,
}: Props<T>) => {
  const { t, i18n: { language } } = useTranslation();

  const handleDateFilterChange = (date: Date[]) => {
    resetPagination?.();
    setFilters((prev) => ({
      ...prev,
      startDate: formatDate(date?.[0], DATE_FORMAT.YYYY_MM_DD),
      endDate: formatDate(date?.[1], DATE_FORMAT.YYYY_MM_DD),
    }));
  };

  const startDate = filters?.startDate ? new Date(filters?.startDate) : null;
  const endDate = filters?.endDate ? new Date(filters?.endDate) : null;

  const resetFilter = (key: string) => {
    if (key === "date") {
      setFilters((prev) => ({
        ...prev,
        startDate: defaultFilters?.startDate,
        endDate: defaultFilters?.endDate,
      }));
    } else {
      const value = defaultFilters?.[key as keyof typeof defaultFilters];
      setFilters((prev) => ({ ...prev, key: value }));
    }
    resetPagination?.();
  };

  const displayFilters = useMemo(() => {
    const { startDate, endDate, ...rest } = filters;
    return {
      date: `${startDate} - ${endDate}`,
      ...rest,
    };
  }, [filters]);

  const hasDateFilter = useMemo(() => {
    return (filters.startDate !== defaultFilters?.startDate) || (filters.endDate !== defaultFilters?.endDate);
  }, [filters, defaultFilters]);

  return (
    <div className="full-width mb-16 filter-panel">
      <div className="flexbox">
        <Calendar
          isRange
          selectedValue={[startDate, endDate]}
          defaultValue={[
            new Date(defaultFilters?.startDate as string),
            new Date(defaultFilters?.endDate as string)
          ]}
          hasInputs
          applyButtonText={t("general.apply")}
          resetButtonText={t("general.reset")}
          maxDate={maxDate}
          minDate={new Date("2018")}
          dateFormat={DATE_FORMAT.dd_MM_yyyy as TDateFormat}
          locale={language}
          onChange={(v) => handleDateFilterChange(v as Date[])}
          showApplyButtons
        >
          <Button
            type="secondary"
            iconProps={{
              Component: IconCalendarRight,
              alignment: "left",
            }}
            buttonText={t("general.setDate")}
          />
        </Calendar>
        {children}
      </div>
      <div className="mt-16 flexbox filter-panel-chips">
        {Object.entries(displayFilters).map(([key, value]) => (
          <div key={key} className="mr-16">
            <Chips
              onClick={() => resetFilter(key)}
              withAction={!(key === "date" && !hasDateFilter)}
              type="outlined"
              className={classNames({ "chip-without-action": key === "date" && !hasDateFilter })}
              text={value}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
